/**
 * W1-11a (app-registry-keymap): the keyboard router (ARCHITECTURE.md §6.3, DESIGN.md §8.1).
 *
 * Listens on `window` in the capture phase (keydown, keyup, wheel) and routes chords through
 * `keymap.candidates` -> `registry.runAny(..., 'key', event)`. `preventDefault` (and stopPropagation) only when a
 * binding matched. The WhenContext is built from the DOM (focus / hover `data-region` atoms, text input, dialogs),
 * the services (selection kinds, focused column, placement, gesture, playing, active line, workspace, level),
 * the keymap (preset, os), module atoms and flags the shell sets (`setAtom('paletteOpen', true)`).
 *
 * Rules:
 *  - Text fields consume printable and editing keys (D§8.1); keys pass only after the field had its chance (bubble
 *    phase, if it did not preventDefault):
 *      - Esc, Tab, Enter (single-line fields) and Ctrl+Enter reach every binding;
 *      - undo (Ctrl+Z) stays in the field while it has its own history (its text differs from the last committed
 *        text: focus, Enter, change), then reaches the app;
 *      - under the former 'design' rule nothing else passes. The 'extended' rule (DESIGN.md §8.1 since change
 *        request CR-W1-11a-4; the default DEFAULT_TEXT_FIELD_KEYS) also passes redo
 *        (Ctrl+Shift+Z, Ctrl+Y; in the field while it has undone steps), and F-keys and Ctrl chords the field does
 *        not use itself (not select all, clipboard, caret, word or deletion keys, nor bold/italic/underline in rich
 *        text), which reach app-level commands only (File, View, Playback, Help: save, palette, workspaces, play) or
 *        bindings whose condition requires `textInput`. Commands that act on the selection never fire from a field.
 *  - Composite widgets (menus, listboxes, sliders, tabs, radio groups, grids, selects) and `[data-keys="native"]`
 *    get unmodified keys first (bubble phase); Ctrl/Alt chords are routed in the capture phase.
 *  - While a dialog has focus or a modal is open, only bindings whose condition requires `dialog` apply.
 *  - Focus-visible rule: Space and Enter go to a focused control only when it got focus from the keyboard; after a
 *    pointer focus they reach the global bindings (play, curve picker) and the control's activation is suppressed.
 *    Keyboard focus is FocusRegions' (`focus` dep, one modality tracker for the app), else tracked here.
 *  - Hold bindings (`hold:` chords or commands with kind 'hold') run on keydown and release on keyup (auto-repeat
 *    ignored; window blur or a hidden tab releases them). Release only follows a run that happened: a refused run
 *    (condition false, `enabled` reason, failure) gets no release. Other commands ignore auto-repeat unless
 *    `repeat: true`.
 *  - Wheel chords (`Ctrl+Wheel`, ...): deltaY normalised to pixels and scaled by settings.editing.scrollSensitivity;
 *    the command receives `args.wheel` (WheelInfo) merged into object args. Wheel over a text field or
 *    `[data-wheel="native"]` is never routed.
 *  - Cost: an event whose chords have no binding at all returns before any context is built, and the DOM-derived
 *    parts of the context (regions, dialogs, visibility) are computed only when a binding condition reads them.
 *  - Suspended (W3-01): while <html> carries KEYS_SUSPENDED_ATTR (the `#/dev/keys` keyboard check records raw
 *    presses), no key or wheel is routed.
 */

import type { Json } from '../../core/util/types';
import { FUNCTION_CODES, isBareModifier, isHoldChord, normalizeWheel, type WheelInfo } from '../registry/chords';
import type { AppKeymap, KeymapEntry } from '../registry/keymapCore';
import type { LayoutLabels } from '../registry/layoutLabels';
import type { AppRegistry } from '../registry/registryCore';
import type { AppServices, Chord, CommandCategory, CommandId, WhenContext } from '../registry/types';
import { buildWhenContext } from '../registry/when';
import { activeRegionsOf, focusRegions, isElementVisible, type FocusRegions } from './FocusRegions';

/** Attribute on <html> that turns the router off (the keyboard check page records every press itself). */
export const KEYS_SUSPENDED_ATTR = 'data-keys-suspended';

/**
 * Text-field pass-through rule: 'design' = DESIGN.md §8.1 as written (only Esc, Enter, Tab, Ctrl+Enter and Ctrl+Z,
 * undo inside the field first); 'extended' = change request W1-11a CR-4 (also redo, F-keys and the Ctrl chords the
 * field does not use itself, restricted to app-level commands).
 */
export type TextFieldKeys = 'design' | 'extended';

/** Pass-through rule in force: DESIGN.md §8.1 as written (accepting change request CR-4 is this one value). */
export const DEFAULT_TEXT_FIELD_KEYS: TextFieldKeys = 'extended';

export interface KeyboardRouterDeps {
  readonly registry: Pick<AppRegistry, 'command' | 'runAny' | 'release' | 'moduleAtoms'>;
  readonly keymap: Pick<
    AppKeymap,
    'candidates' | 'hasBindings' | 'eventChords' | 'wheelChord' | 'requires' | 'preset' | 'os'
  >;
  /** Current services (null before they exist). */
  readonly services: () => AppServices | null;
  readonly window?: Window;
  /** Learns the user's layout from key presses (Firefox labels). */
  readonly labels?: Pick<LayoutLabels, 'learn'>;
  /**
   * The app's input-modality tracker (`focusRegions`, installed). When given, the `:focus-visible` rule and
   * `focusedByKeyboard` ask it, so the router and the focus hand-back never disagree; otherwise the router tracks
   * keyboard focus itself. `installKeyboardRouter` defaults it to the app controller.
   */
  readonly focus?: Pick<FocusRegions, 'focusedByKeyboard'>;
  /** Text-field pass-through rule (default DEFAULT_TEXT_FIELD_KEYS). */
  readonly textFieldKeys?: TextFieldKeys;
}

export interface KeyboardRouter {
  /** The current editor context (also used by the registry for command checks). */
  whenContext(): WhenContext;
  /** Force an atom on/off (shell flags: 'paletteOpen', 'dialog', 'gesture', or a module's own). */
  setAtom(atom: string, on: boolean): void;
  /** Was this element (default: the focused one) focused from the keyboard (`:focus-visible` rule)? */
  focusedByKeyboard(el?: Element | null): boolean;
  /** Commands of hold bindings currently held down. */
  held(): readonly CommandId[];
  /** Start listening; returns the stop function (idempotent). */
  install(): () => void;
  dispose(): void;
}

const TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
  '',
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
]);

const COMPOSITE =
  '[role="menu"], [role="menubar"], [role="listbox"], [role="tree"], [role="treegrid"], [role="grid"], ' +
  '[role="tablist"], [role="radiogroup"], [role="slider"], [role="spinbutton"], [role="combobox"], select, ' +
  'input[type="range"], [data-keys="native"]';

const CONTROL =
  'button, a[href], summary, input[type="checkbox"], input[type="radio"], input[type="button"], ' +
  'input[type="submit"], input[type="reset"], input[type="file"], input[type="color"], [role="button"], ' +
  '[role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="menuitem"], [role="menuitemcheckbox"], ' +
  '[role="menuitemradio"], [role="option"], [role="link"], [role="treeitem"]';

/** Ctrl/Cmd keys a text field uses itself: select all, clipboard, caret and word moves, deletion, input switching. */
const FIELD_PRIMARY_CODES: ReadonlySet<string> = new Set([
  'KeyA',
  'KeyC',
  'KeyX',
  'KeyV',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Backspace',
  'Delete',
  'Insert',
  'Space',
]);

/** Ctrl/Cmd keys rich-text fields (contenteditable) also use: bold, italic, underline. */
const RICH_TEXT_CODES: ReadonlySet<string> = new Set(['KeyB', 'KeyI', 'KeyU']);

/** Commands that may run from inside a text field (they do not act on the selection or the field's subject). */
const FIELD_SAFE_CATEGORIES: ReadonlySet<CommandCategory> = new Set<CommandCategory>([
  'File',
  'View',
  'Playback',
  'Help',
]);

/** Form fields by tag name (works for elements of other windows too, e.g. a pop-out preview). */
function inputOf(el: Element): HTMLInputElement | null {
  return el.tagName === 'INPUT' ? (el as HTMLInputElement) : null;
}

function textAreaOf(el: Element): HTMLTextAreaElement | null {
  return el.tagName === 'TEXTAREA' ? (el as HTMLTextAreaElement) : null;
}

/** Element whose own keyboard handling is text editing. */
export function isTextInput(el: Element | null): boolean {
  if (el === null) return false;
  const area = textAreaOf(el);
  if (area !== null) return !area.readOnly && !area.disabled;
  const input = inputOf(el);
  if (input !== null) return TEXT_INPUT_TYPES.has(input.type) && !input.readOnly && !input.disabled;
  if (el.closest('[contenteditable]:not([contenteditable="false"])') !== null) return true;
  const role = el.getAttribute('role');
  return role === 'textbox' || role === 'searchbox';
}

function isRichText(el: Element): boolean {
  return inputOf(el) === null && textAreaOf(el) === null;
}

function isMultiline(el: Element): boolean {
  if (textAreaOf(el) !== null) return true;
  return inputOf(el) === null && el.getAttribute('aria-multiline') !== 'false';
}

function fieldValue(el: Element): string {
  return (inputOf(el) ?? textAreaOf(el))?.value ?? el.textContent;
}

/** A widget that uses arrows / typing itself (menus, lists, sliders, tabs, ...), not a region root. */
function compositeOf(el: Element | null): Element | null {
  if (el === null) return null;
  if (el.closest('[data-keys="global"]') !== null) return null;
  const c = el.closest(COMPOSITE);
  return c !== null && !c.hasAttribute('data-region') ? c : null;
}

function isControl(el: Element | null): boolean {
  return el !== null && !isTextInput(el) && el.matches(CONTROL);
}

/** Args for a wheel-bound command: `wheel` merged into object args (others are passed unchanged). */
export function mergeWheelArgs(args: Json | undefined, wheel: WheelInfo): Json {
  const w: Json = { ...wheel };
  if (args === undefined) return { wheel: w };
  if (args !== null && typeof args === 'object' && !Array.isArray(args))
    return { ...(args as { readonly [k: string]: Json }), wheel: w };
  return args;
}

/** How a key from a text field may be routed: to every binding, or only to commands safe inside a field. */
type FieldScope = 'all' | 'fieldSafe';

/** A started hold: the command and whether its run happened (release only follows a run). */
interface HeldKey {
  readonly command: CommandId;
  readonly started: Promise<boolean>;
}

export function createKeyboardRouter(deps: KeyboardRouterDeps): KeyboardRouter {
  const win = deps.window ?? window;
  const doc = win.document;
  const flags = new Set<string>();
  const keyboardFocused = new WeakSet<Element>();
  /** Text of each field when it was focused or last committed (Enter, change): its own undo applies while it differs. */
  const fieldBaseline = new WeakMap<Element, string>();
  /** Undone steps a field can redo itself (historyUndo / historyRedo input events; other input clears them). */
  const fieldRedo = new WeakMap<Element, number>();
  /** Keys deferred to the bubble phase, with the commands they may reach. */
  const deferred = new WeakMap<KeyboardEvent, FieldScope>();
  /** Held hold-bindings by KeyboardEvent.code. */
  const held = new Map<string, HeldKey>();
  /** Codes whose keyup default must be prevented (Space/Enter routed away from a pointer-focused control). */
  const suppressKeyup = new Set<string>();
  let modality: 'keyboard' | 'pointer' = 'pointer';
  const fieldKeys = deps.textFieldKeys ?? DEFAULT_TEXT_FIELD_KEYS;
  /** `:focus-visible` rule: the shared tracker when given, else the router's own. */
  const isKeyboardFocused = (el: Element): boolean =>
    deps.focus === undefined ? keyboardFocused.has(el) : deps.focus.focusedByKeyboard(el);
  let hoverEl: Element | null = null;
  let lastRegionEl: Element | null = null;
  let installed: (() => void) | null = null;

  const activeElement = (): Element | null => {
    const a = doc.activeElement;
    return a === null || a === doc.body || a === doc.documentElement ? null : a;
  };

  /** Element whose regions count as focused: the focused element, else the last focused region (still shown). */
  const focusAnchor = (): Element | null => {
    const a = activeElement();
    if (a !== null) return a;
    return lastRegionEl !== null && lastRegionEl.isConnected && isElementVisible(lastRegionEl) ? lastRegionEl : null;
  };

  const dialogOpen = (active: Element | null): boolean => {
    if (flags.has('dialog')) return true;
    if (active?.closest('[role="dialog"], [role="alertdialog"]') != null) return true;
    return [...doc.querySelectorAll('[aria-modal="true"]')].some(isElementVisible);
  };

  const whenContext = (): WhenContext => {
    const active = activeElement();
    // DOM-derived inputs are computed on first use only (most conditions never read regions or dialogs).
    return buildWhenContext({
      services: deps.services(),
      focusRegions: () => activeRegionsOf(focusAnchor()),
      hoverRegions: () => activeRegionsOf(hoverEl?.isConnected === true ? hoverEl : null),
      textInput: () => isTextInput(active),
      dialog: () => dialogOpen(active),
      flags,
      moduleAtoms: deps.registry.moduleAtoms(),
      route: win.location.hash,
      preset: deps.keymap.preset,
      os: deps.keymap.os,
    });
  };

  const scrollSensitivity = (): number => {
    try {
      return deps.services()?.settings.value.editing.scrollSensitivity ?? 1;
    } catch {
      return 1;
    }
  };

  const primary = (e: KeyboardEvent): boolean => (deps.keymap.os === 'mac' ? e.metaKey : e.ctrlKey);

  const isUndo = (e: KeyboardEvent): boolean => primary(e) && !e.altKey && e.code === 'KeyZ' && !e.shiftKey;
  const isRedo = (e: KeyboardEvent): boolean =>
    primary(e) && !e.altKey && ((e.code === 'KeyZ' && e.shiftKey) || (e.code === 'KeyY' && !e.shiftKey));

  /** Does the field still have its own undo (text changed since the baseline) or redo (undone steps) to do? */
  const fieldHasHistory = (e: KeyboardEvent, field: Element): boolean =>
    isUndo(e)
      ? fieldBaseline.get(field) !== fieldValue(field)
      : (fieldRedo.get(field) ?? 0) > 0 || fieldBaseline.get(field) !== fieldValue(field);

  /** Which bindings a key pressed in a text field may reach (after the field's own handlers), or null: none. */
  const textPassScope = (e: KeyboardEvent, field: Element): FieldScope | null => {
    if (e.altKey) return null;
    const mod = primary(e);
    switch (e.code) {
      case 'Escape':
      case 'Tab':
        return mod ? null : 'all';
      case 'Enter':
      case 'NumpadEnter':
        return (mod ? !e.shiftKey : !e.shiftKey && !isMultiline(field)) ? 'all' : null;
      default:
        break;
    }
    if (isUndo(e)) return fieldHasHistory(e, field) ? null : 'all';
    if (fieldKeys === 'design') return null;
    if (isRedo(e)) return fieldHasHistory(e, field) ? null : 'all';
    if (FUNCTION_CODES.has(e.code)) return 'fieldSafe';
    if (!mod) return null;
    if (FIELD_PRIMARY_CODES.has(e.code)) return null;
    if (RICH_TEXT_CODES.has(e.code) && isRichText(field)) return null;
    return 'fieldSafe';
  };

  type Mode = 'route' | 'bubble' | 'native';

  const classify = (
    e: KeyboardEvent,
    active: Element | null,
  ): { readonly mode: Mode; readonly onControl: boolean; readonly scope: FieldScope } => {
    if (active !== null && isTextInput(active)) {
      const scope = textPassScope(e, active);
      return scope === null
        ? { mode: 'native', onControl: false, scope: 'all' }
        : { mode: 'bubble', onControl: false, scope };
    }
    if (compositeOf(active) !== null) {
      return { mode: primary(e) || e.altKey ? 'route' : 'bubble', onControl: false, scope: 'all' };
    }
    const activation = e.code === 'Space' || e.code === 'Enter' || e.code === 'NumpadEnter';
    if (activation && isControl(active)) {
      if (active !== null && isKeyboardFocused(active)) return { mode: 'native', onControl: false, scope: 'all' };
      return { mode: 'route', onControl: true, scope: 'all' };
    }
    return { mode: 'route', onControl: false, scope: 'all' };
  };

  const allowed = (entry: KeymapEntry, dialog: boolean, scope: FieldScope): boolean => {
    if (dialog && !deps.keymap.requires(entry, 'dialog')) return false;
    if (scope === 'all') return true;
    if (deps.keymap.requires(entry, 'textInput')) return true;
    const category = deps.registry.command(entry.binding.command)?.category;
    return category !== undefined && FIELD_SAFE_CATEGORIES.has(category);
  };

  const pick = (chord: Chord | null, ctx: WhenContext, dialog: boolean, scope: FieldScope): KeymapEntry | undefined => {
    if (chord === null) return undefined;
    return deps.keymap.candidates(chord, ctx).find((entry) => allowed(entry, dialog, scope));
  };

  const route = (e: KeyboardEvent, onControl: boolean, scope: FieldScope): void => {
    const chords = deps.keymap.eventChords(e);
    // Nothing bound to either chord in any context: no context to build.
    if (!deps.keymap.hasBindings(chords.code) && !deps.keymap.hasBindings(chords.char)) return;
    const ctx = whenContext();
    const dialog = ctx.has('dialog');
    const entry = pick(chords.code, ctx, dialog, scope) ?? pick(chords.char, ctx, dialog, scope);
    if (entry === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    if (onControl) suppressKeyup.add(e.code);
    const { command, args } = entry.binding;
    const def = deps.registry.command(command);
    if (isHoldChord(entry.binding.chord) || def?.kind === 'hold') {
      if (e.repeat || held.has(e.code)) return;
      const hold: HeldKey = { command, started: deps.registry.runAny(command, args, 'key', e) };
      held.set(e.code, hold);
      // A refused run (condition, `enabled` reason, failure) is no hold: forget it, so no release follows.
      void hold.started.then((ran) => {
        if (!ran && held.get(e.code) === hold) held.delete(e.code);
      });
      return;
    }
    if (e.repeat && def?.repeat !== true) return;
    void deps.registry.runAny(command, args, 'key', e);
  };

  /** Release a hold once its run has happened (never for a run that was refused). */
  const release = (hold: HeldKey, e?: KeyboardEvent): void => {
    void hold.started.then((ran) => {
      if (ran) deps.registry.release(hold.command, 'key', e);
    });
  };

  const releaseAll = (): void => {
    for (const hold of held.values()) release(hold);
    held.clear();
  };

  /** After a commit key in a field (Enter, Ctrl+Enter), its text is the new baseline once the commit re-rendered. */
  const rebaselineSoon = (field: Element): void => {
    win.setTimeout(() => {
      if (!field.isConnected) return;
      fieldBaseline.set(field, fieldValue(field));
      fieldRedo.delete(field);
    }, 0);
  };

  const suspended = (): boolean => win.document.documentElement.hasAttribute(KEYS_SUSPENDED_ATTR);

  const onKeyDownCapture = (e: KeyboardEvent): void => {
    if (suspended()) return;
    if (!isBareModifier(e)) modality = 'keyboard';
    deps.labels?.learn(e);
    const active = activeElement();
    if (
      active !== null &&
      (e.code === 'Enter' || e.code === 'NumpadEnter') &&
      !e.isComposing &&
      isTextInput(active) &&
      (primary(e) || !isMultiline(active))
    ) {
      rebaselineSoon(active);
    }
    if (e.defaultPrevented) return;
    const { mode, onControl, scope } = classify(e, active);
    if (mode === 'native') return;
    if (mode === 'bubble') {
      deferred.set(e, scope);
      return;
    }
    route(e, onControl, scope);
  };

  const onKeyDownBubble = (e: KeyboardEvent): void => {
    const scope = deferred.get(e);
    if (scope === undefined) return;
    deferred.delete(e);
    if (e.defaultPrevented) return;
    route(e, false, scope);
  };

  const onKeyUpCapture = (e: KeyboardEvent): void => {
    if (suspended()) return;
    const hold = held.get(e.code);
    if (hold !== undefined) {
      held.delete(e.code);
      e.preventDefault();
      release(hold, e);
    }
    if (suppressKeyup.delete(e.code)) e.preventDefault();
  };

  const onWheel = (e: WheelEvent): void => {
    if (e.defaultPrevented || suspended()) return;
    const target = e.target instanceof Element ? e.target : null;
    if (target !== null) hoverEl = target;
    const chord = deps.keymap.wheelChord(e);
    // Most wheel events (plain scrolling) have no binding: return before any DOM or context work.
    if (!deps.keymap.hasBindings(chord)) return;
    if (
      target !== null &&
      (target.closest('[data-wheel="native"]') !== null || (isTextInput(target) && target === activeElement()))
    ) {
      return;
    }
    const ctx = whenContext();
    const entry = pick(chord, ctx, ctx.has('dialog'), 'all');
    if (entry === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    const wheel = normalizeWheel(e, {
      os: deps.keymap.os,
      sensitivity: scrollSensitivity(),
      pageHeightPx: win.innerHeight || 800,
    });
    void deps.registry.runAny(entry.binding.command, mergeWheelArgs(entry.binding.args, wheel), 'key', e);
  };

  /** Track each field's own redo steps (the browser's undo stack of that field). */
  const onInput = (e: Event): void => {
    const target = e.target;
    if (!(target instanceof Element) || !isTextInput(target)) return;
    const type = (e as InputEvent).inputType as string | undefined;
    if (type === 'historyUndo') fieldRedo.set(target, (fieldRedo.get(target) ?? 0) + 1);
    else if (type === 'historyRedo') fieldRedo.set(target, Math.max(0, (fieldRedo.get(target) ?? 0) - 1));
    else fieldRedo.delete(target);
  };

  /** A committed field (change event): its text is the new baseline. */
  const onChange = (e: Event): void => {
    const target = e.target;
    if (target instanceof Element && isTextInput(target)) {
      fieldBaseline.set(target, fieldValue(target));
      fieldRedo.delete(target);
    }
  };

  const onFocusIn = (e: FocusEvent): void => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    if (modality === 'keyboard') keyboardFocused.add(target);
    else keyboardFocused.delete(target);
    if (isTextInput(target)) {
      fieldBaseline.set(target, fieldValue(target));
      fieldRedo.delete(target);
    }
    const region = target.closest('[data-region]');
    if (region !== null) lastRegionEl = region;
  };

  const onPointerDown = (e: PointerEvent): void => {
    modality = 'pointer';
    if (e.target instanceof Element) hoverEl = e.target;
  };

  const onPointerOver = (e: PointerEvent): void => {
    hoverEl = e.target instanceof Element ? e.target : null;
  };

  const onPointerOut = (e: PointerEvent): void => {
    if (e.relatedTarget === null) hoverEl = null;
  };

  /** The window lost focus (blur does not bubble, so only the window's own blur arrives here). */
  const onBlur = (): void => {
    releaseAll();
  };

  const onVisibility = (): void => {
    if (doc.visibilityState === 'hidden') releaseAll();
  };

  const router: KeyboardRouter = {
    whenContext,
    setAtom(atom, on) {
      if (on) flags.add(atom);
      else flags.delete(atom);
    },
    focusedByKeyboard(el = doc.activeElement) {
      return el !== null && isKeyboardFocused(el);
    },
    held: () => [...held.values()].map((h) => h.command),
    install() {
      if (installed !== null) return installed;
      const capture = { capture: true } as const;
      win.addEventListener('keydown', onKeyDownCapture, capture);
      win.addEventListener('keydown', onKeyDownBubble);
      win.addEventListener('keyup', onKeyUpCapture, capture);
      win.addEventListener('wheel', onWheel, { capture: true, passive: false });
      win.addEventListener('focusin', onFocusIn, capture);
      win.addEventListener('input', onInput, capture);
      win.addEventListener('change', onChange, capture);
      win.addEventListener('pointerdown', onPointerDown, capture);
      win.addEventListener('pointerover', onPointerOver, capture);
      win.addEventListener('pointerout', onPointerOut, capture);
      win.addEventListener('blur', onBlur);
      doc.addEventListener('visibilitychange', onVisibility);
      const stop = (): void => {
        releaseAll();
        win.removeEventListener('keydown', onKeyDownCapture, capture);
        win.removeEventListener('keydown', onKeyDownBubble);
        win.removeEventListener('keyup', onKeyUpCapture, capture);
        win.removeEventListener('wheel', onWheel, capture);
        win.removeEventListener('focusin', onFocusIn, capture);
        win.removeEventListener('input', onInput, capture);
        win.removeEventListener('change', onChange, capture);
        win.removeEventListener('pointerdown', onPointerDown, capture);
        win.removeEventListener('pointerover', onPointerOver, capture);
        win.removeEventListener('pointerout', onPointerOut, capture);
        win.removeEventListener('blur', onBlur);
        doc.removeEventListener('visibilitychange', onVisibility);
        installed = null;
      };
      installed = stop;
      return stop;
    },
    dispose() {
      installed?.();
    },
  };
  return router;
}

/**
 * Create and install the app's router (the shell calls this once at startup and passes `whenContext` to the
 * registry). Unless `focus` is given, the router on the app's own window shares the app FocusRegions controller
 * (installed here if it is not yet), so there is one input-modality tracker.
 */
export function installKeyboardRouter(deps: KeyboardRouterDeps): KeyboardRouter {
  let focus = deps.focus;
  if (focus === undefined && (deps.window === undefined || deps.window === globalThis.window)) {
    focusRegions.install();
    focus = focusRegions;
  }
  const router = createKeyboardRouter(focus === undefined ? deps : { ...deps, focus });
  router.install();
  return router;
}
